import { ui } from "@patient-watch/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { useProfileCopy } from "@/features/i18n/profileCopy";

const ROW_SHAPES = [
    { name: "w-36", email: "w-44", role: "w-16", org: "w-28", extra: true },
    { name: "w-28", email: "w-52", role: "w-20", org: "w-24", extra: false },
    { name: "w-40", email: "w-40", role: "w-14", org: "w-32", extra: true },
    { name: "w-32", email: "w-48", role: "w-16", org: "w-20", extra: false },
    { name: "w-24", email: "w-56", role: "w-20", org: "w-36", extra: false },
    { name: "w-36", email: "w-36", role: "w-16", org: "w-24", extra: true },
] as const;

function HeaderLabel({ label }: { label: string }) {
    return <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">{label}</span>;
}

function TeamMobileCardSkeleton({ name, email, role, org, extra }: (typeof ROW_SHAPES)[number]) {
    return (
        <div className="bg-card shadow-sm p-3 border border-border/60 border-l-[3px] border-l-transparent rounded-lg w-full">
            <div className="flex flex-col items-stretch gap-2 min-w-0">
                <div className="flex items-start gap-3">
                    <ui.Skeleton className="bg-slate-200/80 rounded-full w-10 h-10 shrink-0" />
                    <div className="flex-1 space-y-2 pt-0.5 min-w-0">
                        <ui.Skeleton className={`bg-slate-200/80 h-5 ${name}`} />
                        <ui.Skeleton className={`bg-slate-200/70 h-3 ${email}`} />
                    </div>
                    <ui.Skeleton className="bg-slate-200/70 rounded-md w-8 h-8 shrink-0" />
                </div>
                <div className="flex flex-wrap gap-1">
                    <ui.Skeleton className={`bg-slate-200/80 h-5 ${role} rounded-full`} />
                    <ui.Skeleton className={`bg-slate-200/80 h-5 ${org} rounded-full`} />
                    {extra ? <ui.Skeleton className="bg-slate-200/80 rounded-full w-16 h-5" /> : null}
                </div>
            </div>
        </div>
    );
}

function TeamTableRowSkeleton({ name, email, role, org }: (typeof ROW_SHAPES)[number]) {
    return (
        <TableRow className="hover:bg-transparent">
            <TableCell>
                <div className="flex items-center gap-3 min-w-0">
                    <ui.Skeleton className="bg-slate-200/80 rounded-full w-8 h-8 shrink-0" />
                    <div className="space-y-1.5 min-w-0">
                        <ui.Skeleton className={`bg-slate-200/80 h-4 ${name}`} />
                        <ui.Skeleton className={`bg-slate-200/70 h-3.5 ${email}`} />
                    </div>
                </div>
            </TableCell>
            <TableCell>
                <ui.Skeleton className={`bg-slate-200/80 h-5 ${role} rounded-full`} />
            </TableCell>
            <TableCell>
                <ui.Skeleton className={`bg-slate-200/80 h-5 ${org} rounded-full`} />
            </TableCell>
            <TableCell>
                <ui.Skeleton className="bg-slate-200/80 rounded-full w-20 h-5" />
            </TableCell>
            <TableCell>
                <ui.Skeleton className="bg-slate-200/80 rounded-full w-16 h-5" />
            </TableCell>
            <TableCell>
                <ui.Skeleton className="bg-slate-200/70 rounded-md w-8 h-8" />
            </TableCell>
            <TableCell>
                <ui.Skeleton className="bg-slate-200/70 rounded-md w-8 h-8" />
            </TableCell>
        </TableRow>
    );
}

export function TeamTableSkeleton() {
    const copy = useProfileCopy();

    return (
        <div aria-busy="true" aria-live="polite" data-testid="team-table-skeleton">
            <span className="sr-only">{copy.loading}</span>
            <div className="flex items-center gap-2 mb-4 w-full min-w-0">
                <ui.Skeleton className="bg-slate-200/70 w-[240px] max-w-full h-12" />
                <ui.Skeleton className="md:hidden bg-slate-200/70 rounded-md w-12 h-12 shrink-0" />
                <ui.Skeleton className="hidden md:block bg-slate-200/70 rounded-md w-24 h-12 shrink-0" />
                <ui.Skeleton className="hidden md:block bg-slate-200/70 rounded-md w-28 h-12 shrink-0" />
                <ui.Skeleton className="md:hidden bg-slate-200/70 rounded-md w-12 h-12 shrink-0" />
            </div>

            <div className="md:hidden flex flex-col gap-2 w-full">
                {ROW_SHAPES.map((shape, index) => (
                    <TeamMobileCardSkeleton key={index} {...shape} />
                ))}
            </div>

            <div className="hidden md:block bg-white dark:bg-slate-950 border rounded-md overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="hover:bg-transparent">
                            <TableHead>
                                <HeaderLabel label={copy.columnName} />
                            </TableHead>
                            <TableHead>{copy.columnRole}</TableHead>
                            <TableHead>{copy.columnOrganisation}</TableHead>
                            <TableHead>
                                <HeaderLabel label={copy.columnAddedBy} />
                            </TableHead>
                            <TableHead>
                                <HeaderLabel label={copy.columnDefaultSecretary} />
                            </TableHead>
                            <TableHead>
                                <span className="sr-only">{copy.columnFavourite}</span>
                            </TableHead>
                            <TableHead>
                                <span className="sr-only">{copy.edit}</span>
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {ROW_SHAPES.map((shape, index) => (
                            <TeamTableRowSkeleton key={index} {...shape} />
                        ))}
                    </TableBody>
                </Table>
            </div>

            <div className="py-3">
                <ui.Skeleton className="bg-slate-200/70 w-32 h-4" />
            </div>
        </div>
    );
}
